import React, { useEffect, useRef } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";

/**
 * Phase 1 – Mapbox map (read-only)
 * Public Find Family Doctor
 * Pins represent clinics
 */

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN || "";

export type MapClinicItem = {
  ClinicID: number;
  ClinicName?: string | null;

  // ✅ Support BOTH legacy and current API key names
  Latitude?: number | null;
  Longitude?: number | null;
  ClinicLatitude?: number | null;
  ClinicLongitude?: number | null;

  // Optional enrichers (doctor-row data)
  DoctorName?: string | null;
  Gender?: string | null;
  Phone?: string | null;

  // Optional: used for doctor profile links in popup list
  FamilyDoctorID?: string | number | null;

  // ✅ NEW (optional): clinic address if caller provides it
  ClinicAddress?: string | null;

  // (Optional) other address variants if caller provides them
  ClinicFullAddress?: string | null;
  ClinicAddressText?: string | null;
  ClinicAddressLine?: string | null;
  Address?: string | null;
  AddressText?: string | null;

  // Also allow clinic phone variants
  ClinicPhone?: string | null;
};

const DEFAULT_CENTER: [number, number] = [-79.3832, 43.6532]; // Toronto
const DEFAULT_ZOOM = 9;

type Props = {
  items: MapClinicItem[];
  onClinicSelect?: (clinicId: number) => void; // parent scroll/highlight
  center?: [number, number] | null; // [lng, lat] - optional center point (e.g., from postal code search)
};

function escapeHtml(input: any): string {
  const s = String(input ?? "");
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function pickFirstString(obj: any, keys: string[]): string {
  for (const k of keys) {
    const v = obj?.[k];
    if (v !== undefined && v !== null && String(v).trim().length > 0)
      return String(v).trim();
  }
  return "";
}

export default function FamilyDoctorMap({ items, onClinicSelect, center }: Props) {
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const markersRef = useRef<mapboxgl.Marker[]>([]);

  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;

    // Mapbox won't render if the container has 0 height.
    const el = mapContainerRef.current;
    const computed = window.getComputedStyle(el);
    const h = parseFloat(computed.height || "0");
    if (!Number.isFinite(h) || h <= 0) {
      el.style.height = "420px";
    }

    // Don't crash if token is missing
    if (!mapboxgl.accessToken || mapboxgl.accessToken.trim().length === 0) return;

    mapRef.current = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: "mapbox://styles/mapbox/streets-v11",
      center: DEFAULT_CENTER,
      zoom: DEFAULT_ZOOM,
    });

    mapRef.current.addControl(new mapboxgl.NavigationControl(), "top-right");

    return () => {
      markersRef.current.forEach((m) => m.remove());
      markersRef.current = [];
      mapRef.current?.remove();
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!mapRef.current || !center) return;
    const [lng, lat] = center;
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) return;

    mapRef.current.flyTo({
      center: [lng, lat],
      zoom: 11,
      duration: 1500,
    });
  }, [center]);

  useEffect(() => {
    if (!mapRef.current) return;

    markersRef.current.forEach((m) => m.remove());
    markersRef.current = [];

    const byClinic = new Map<number, MapClinicItem[]>();

    (Array.isArray(items) ? items : []).forEach((row) => {
      const id = Number(row?.ClinicID);
      if (!Number.isFinite(id) || id <= 0) return;

      // ✅ Accept current API keys and legacy keys
      const lat = (row as any)?.ClinicLatitude ?? (row as any)?.Latitude;
      const lng = (row as any)?.ClinicLongitude ?? (row as any)?.Longitude;

      if (
        typeof lat !== "number" ||
        typeof lng !== "number" ||
        !Number.isFinite(lat) ||
        !Number.isFinite(lng)
      ) {
        return;
      }

      const list = byClinic.get(id) || [];
      list.push(row);
      byClinic.set(id, list);
    });

    const points: [number, number][] = [];

    const entries = Array.from(byClinic.entries());
    for (let i = 0; i < entries.length; i++) {
      const clinicId = entries[i][0];
      const rows = entries[i][1];

      const first = rows[0];

      const lat = ((first as any)?.ClinicLatitude ?? (first as any)?.Latitude) as number;
      const lng = ((first as any)?.ClinicLongitude ?? (first as any)?.Longitude) as number;

      points.push([lng, lat]);

      const el = document.createElement("div");
      el.className = "fd-map-marker";
      el.innerHTML = `
        <svg width="28" height="36" viewBox="0 0 28 36" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M14 0C6.268 0 0 6.268 0 14c0 10.5 14 22 14 22s14-11.5 14-22c0-7.732-6.268-14-14-14z" fill="#1669b3"/>
          <circle cx="14" cy="14" r="6" fill="white"/>
        </svg>
      `;
      el.style.cursor = "pointer";
      el.style.transform = "translate(-50%, -100%)";

      const clinicName = escapeHtml(first.ClinicName || "Clinic");

      const addressRaw =
        pickFirstString(first, [
          "ClinicAddress",
          "ClinicFullAddress",
          "ClinicAddressText",
          "ClinicAddressLine",
          "Address",
          "AddressText",
        ]) || "";

      const clinicAddress = escapeHtml(addressRaw);

      const phoneRaw = pickFirstString(first, ["ClinicPhone", "Phone", "phone"]);
      const phone = escapeHtml(phoneRaw);

      const totalDoctors = rows.length;
      const showBtnId = `fd_show_docs_${clinicId}_${Math.random().toString(36).slice(2)}`;

      const html = `
        <div style="min-width:180px;">
          <div style="font-weight:700; font-size:14px; color:#102a43; margin-bottom:8px; line-height:1.3;">${clinicName}</div>

          ${
            clinicAddress || phone
              ? `<div style="display:flex; flex-direction:column; gap:6px; margin-bottom:10px;">
                  ${
                    clinicAddress
                      ? `<div style="display:flex; align-items:flex-start; gap:6px;">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style="flex-shrink:0; margin-top:2px;">
                            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="#6b7a94"/>
                            <circle cx="12" cy="9" r="2.5" fill="white"/>
                          </svg>
                          <span style="font-size:12px; color:#4a5568; line-height:1.4;">${clinicAddress}</span>
                        </div>`
                      : ""
                  }
                  ${
                    phone
                      ? `<div style="display:flex; align-items:center; gap:6px;">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style="flex-shrink:0;">
                            <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" fill="#6b7a94"/>
                          </svg>
                          <a href="tel:${phone}" style="font-size:12px; color:#1669b3; text-decoration:none; font-weight:500;">${phone}</a>
                        </div>`
                      : ""
                  }
                </div>`
              : ""
          }

          <button
            id="${escapeHtml(showBtnId)}"
            type="button"
            style="
              display:block;
              width:100%;
              padding:8px 12px;
              border:none;
              border-radius:6px;
              background:#1669b3;
              color:white;
              font-size:12px;
              font-weight:600;
              cursor:pointer;
              box-sizing:border-box;
            "
            onmouseover="this.style.background='#0f5087'"
            onmouseout="this.style.background='#1669b3'"
            title="Show doctors in results"
          >Show ${totalDoctors} Doctor${totalDoctors === 1 ? "" : "s"} in Results</button>
        </div>
      `;

      const popup = new mapboxgl.Popup({ offset: 12, maxWidth: "280px" }).setHTML(html);

      popup.on("open", () => {
        const btn = document.getElementById(showBtnId);
        if (!btn) return;

        const handler = () => {
          if (typeof onClinicSelect === "function") onClinicSelect(clinicId);
          popup.remove();
        };

        btn.addEventListener("click", handler, { once: true });
      });

      const marker = new mapboxgl.Marker(el)
        .setLngLat([lng, lat])
        .setPopup(popup)
        .addTo(mapRef.current);

      markersRef.current.push(marker);
    }

    if (points.length > 0) {
      const bounds = points.reduce(
        (b, p) => b.extend(p),
        new mapboxgl.LngLatBounds(points[0], points[0])
      );
      mapRef.current.fitBounds(bounds, { padding: 40, maxZoom: 13 });
    }
  }, [items, onClinicSelect]);

  return (
    <div
      ref={mapContainerRef}
      style={{
        width: "100%",
        height: "100%",
        minHeight: 420,
        borderRadius: 12,
        overflow: "hidden",
      }}
    />
  );
}
